import React, { useRef, useState } from "react";
import { Modal } from "../../../components/Modal.jsx";
import { T } from "../../../app/tokens.js";
import { importProjectFinancials } from "../../../data/projectFinancialsImportClient.js";

const HEADER = "app_project,budget,spend_to_date,forecast_at_completion";
const button = { padding: "8px 12px", border: `1px solid ${T.border}`, borderRadius: 6, cursor: "pointer", background: T.white, color: T.dark };

export function ProjectFinancialsImportModal({ onClose, onImported }) {
  const [csv, setCsv] = useState("");
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  function edit(value) { setCsv(value); setPreview(null); setResult(null); setError(""); }
  async function chooseFile(event) {
    if (pending.current) return;
    const file = event.target.files?.[0];
    edit("");
    if (!file) return;
    if (file.size > 256_000) { setError("Choose a CSV file up to 256 KB."); return; }
    pending.current = true; setBusy(true);
    try { edit(await file.text()); }
    catch { setError("Could not read the CSV file."); }
    finally { pending.current = false; setBusy(false); }
  }
  async function submit(mode) {
    if (pending.current || (mode === "apply" && (!preview || preview.errors.length))) return;
    pending.current = true; setBusy(true); setError("");
    try {
      const data = await importProjectFinancials(csv, mode);
      if (mode === "preview") setPreview(data);
      else {
        setResult(data); setPreview(null);
        onImported();
      }
    } catch (err) {
      setError(err.message || "Import failed - refresh Financials and preview before retrying.");
      setPreview(null);
    } finally { pending.current = false; setBusy(false); }
  }
  const report = result || preview;
  return <Modal isOpen onClose={() => { if (!pending.current) onClose(); }} disableBackdropClose={busy}
    title="Import project financials (CSV)" ariaLabelledBy="financials-import-title" testId="financials-import-modal" maxWidth={760}>
    <div style={{ padding: 24, overflowY: "auto" }}>
      <p style={{ color: T.mid, marginTop: 0 }}>Exact project names only - trimmed and case-insensitive. Only eligible, nondeleted projects outside Complete can match. Blank amounts preserve existing values. Zero is a known value. Amounts are USD, ex tax.</p>
      <label>Choose CSV file
        <input aria-label="Choose CSV file" type="file" accept=".csv,text/csv" disabled={busy || !!result} onChange={chooseFile} style={{ display: "block", margin: "8px 0 12px" }} autoFocus />
      </label>
      <label>Or paste CSV
        <textarea aria-label="Or paste CSV" value={csv} disabled={busy || !!result} onChange={event => edit(event.target.value)}
          placeholder={HEADER} rows={6} style={{ display: "block", width: "100%", boxSizing: "border-box", marginTop: 8, padding: 10, border: `1px solid ${T.border}`, borderRadius: 6, color: T.dark, background: T.white }} />
      </label>
      <p style={{ color: T.mid, fontSize: T.fsXs }}>Required header: {HEADER}</p>
      {error && <p role="alert" style={{ color: T.red }}>{error}</p>}
      {report && <div aria-live="polite">
        <p role={result ? "status" : undefined} data-testid={result ? "financials-import-success" : "financials-import-preview"}>
          {result ? "Import complete" : "Preview only - nothing written"} - {report.matched.length} matched, {report.updates} {result ? "updated" : "to update"}, {report.unchanged} unchanged, {report.unmatched.length + report.ambiguous.length} skipped.
        </p>
        {report.errors.length > 0 && <div role="alert" style={{ color: T.red }}>Fix all errors before importing.
          <ul>{report.errors.map((item, i) => <li key={i}>Row {item.row}: {item.message}</li>)}</ul>
        </div>}
        <div style={{ maxHeight: 240, overflowY: "auto" }}>
          {[...report.unmatched, ...report.ambiguous].map(item => <p key={item.row}>Row {item.row}: {item.name} - {item.reason}</p>)}
          {report.matched.map(item => <div key={item.row} style={{ marginBottom: 8 }}>
            <strong>{item.name}</strong> - {item.changes.length ? item.changes.map(change =>
              `${change.field}: ${change.before ?? "unknown"} -> ${change.after}`).join("; ") : "unchanged"}
          </div>)}
        </div>
        {!result && !report.errors.length && <p>Confirm to apply current matches. Unmatched and ambiguous names will be skipped. Matches and values are rechecked on apply.</p>}
      </div>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
        <button type="button" style={button} disabled={busy} onClick={onClose}>{result ? "Done" : "Cancel"}</button>
        {!result && <button type="button" style={button} disabled={busy || !csv.trim()} onClick={() => submit("preview")}>{busy ? "Working..." : "Preview"}</button>}
        {preview && !preview.errors.length && <button type="button" style={{ ...button, background: T.blue, color: T.white }} disabled={busy} onClick={() => submit("apply")}>Confirm import</button>}
      </div>
    </div>
  </Modal>;
}
